import { isTouchDevice, rcLogger } from '@readcloud/common';
import C from 'classnames';

import {
  Annotation,
  DocumentControlActions,
  TextHighlightAnnotation,
  TOCItem,
} from '@readcloud/data';
import ePub from 'epubjs';
import IframeView from 'epubjs/lib/managers/views/iframe';
import 'hammerjs';
import JSZip from 'jszip';
import * as localforage from 'localforage';
import { chain as flatMap } from 'ramda';
import React from 'react';
import {
  Book,
  Contents,
  EpubTOCItem,
  Rendition,
  Section,
  TOC,
} from '../../types/epubs';
import {
  onMarkClicked,
  onRelocated,
  onRendered,
} from '../../utils/epubjsUtils';
import { getPageFlipperWidth } from '../../utils/pageFlipper';

import {
  annotationsActions,
  annotationsSelectors,
  applicationSelectors,
  bookmarksActions,
  bookmarksSelectors,
  readerSelectors,
  reportActions,
  useAppDispatch,
} from '@readcloud/state';
import { useSelector } from 'react-redux';
import { DocumentWithExtrasContext } from '../../context/DocumentWithExtras';
import { TextSelectionContext } from '../../context/TextSelection';
import { fontFacesCss } from '../../fonts';
import { useEpubTextSelectionEventListeners } from '../../hooks/useEpubTextSelectionEventListeners';
import { useEpubFindController } from '../../hooks/useFindController';
import { clearSelection } from '../../utils/selectionHelpers';
import { getPageLabelByString } from '../../utils/utils';
import { SelectionStream } from '../CustomSelection/SelectionStream';
import { ConnectedAnnotationPopover } from '../Modals/ConnectedAnnotationPopover';
import ModalManager from '../Modals/ModalManager';
import { ConnectedLearningToolbar } from '../Toolbars/ConnectedLearningToolbar';
import { EpubReaderProps } from './types';
import { LoadingIcon } from '@readcloud/component-library';

// epubjs's Archive resolves JSZip from the global scope; its `require("jszip")`
// fallback breaks in production builds, so provide the global ourselves.
(window as any).JSZip = JSZip;

const preventDefault = (e: { preventDefault: () => void }) => {
  e.preventDefault();
};
const fontFaceEl = document.createElement('style');
fontFaceEl.innerHTML = fontFacesCss.join('\n');

const userSelectNoneClass = 'user-select-none';
const userSelectNoneEl = document.createElement('style');
userSelectNoneEl.className = userSelectNoneClass;
userSelectNoneEl.innerHTML = `
body{-webkit-user-select:none;-moz-user-select:none;-ms-user-select:none;user-select:none;}
`;

const tapOutTime = 50;
const pointerEvents = ['pointerup', 'pointerdown'];

interface IFrameControlEvent {
  type: 'customTextSelectionUpdate';
}

export const makeCustomIframeView = (
  props: {
    onBeforeDisplay?: (el: HTMLIFrameElement) => void;
    onKeyUp?: (event: KeyboardEvent) => void;
    getCustomTextSelection: () => boolean | undefined;
    onTap: (e: HammerInput, element: HTMLElement) => void;
    onPointerEvent: (e: PointerEvent) => void;
    control: DocumentControlActions;
    nextPage: () => void;
    prevPage: () => void;
  } & Partial<IframeView>
) =>
  class CustomIframeView extends (IframeView as any) {
    public mc?: HammerManager;
    public async load(contents: string) {
      if (!this['iframe']) {
        throw new Error('No Iframe Available');
      }

      if (!this['settings']) {
        return;
      }

      // The load handler must be attached before any content is written
      // (matching epubjs's own IframeView.load): document.close() may fire
      // the iframe's load event synchronously, so attaching the handler
      // afterwards can miss it and strand the view mid-load.
      const epubContents = new Promise((res) => {
        this['iframe'].onload = (event: Event) => {
          this['onLoad'](event, {
            resolve: res,
          });
        };
      });

      if (this['settings']?.method === 'srcdoc') {
        this['iframe'].srcdoc = contents;
        this['element'].appendChild(this['iframe']);
      } else {
        this['element'].appendChild(this['iframe']);

        this['document'] = this['iframe']?.contentDocument || undefined;

        if (!this['document']) {
          throw new Error('No Document Available');
        }

        if (this['document']) {
          this['document'].open();
          this['document'].write(contents);
          this['document'].close();
        }
      }

      return await epubContents;
    }

    public tapListener = (e: HammerInput) => {
      const iframe: HTMLIFrameElement | undefined = this['iframe'];
      props.onTap(e, iframe);
    };

    public updateDisableTextSelect = () => {
      const iframe: HTMLIFrameElement | undefined = this['iframe'];
      const document = iframe && iframe.contentDocument;
      if (!document || !document.head) {
        return;
      }

      if (props.getCustomTextSelection()) {
        document.head.appendChild(userSelectNoneEl);
      } else {
        const el = document.querySelector(`.${userSelectNoneClass}`);
        if (el) {
          el.remove();
        }
      }
    };

    public handleCustomeTextSelection = (e: PointerEvent) => {
      const iframe: HTMLIFrameElement | undefined = this['iframe'];
      if (iframe && iframe.contentDocument) {
        const document = iframe.contentDocument;

        switch (e.pointerType) {
          case 'mouse': {
            props.setCustomTextSelection((val) => {
              return false;
            });
            const el = document.querySelector(`.${userSelectNoneClass}`);
            if (el) {
              el.remove();
            }
            break;
          }
          case 'touch': {
            props.setCustomTextSelection(true);
            const el = document.querySelector(`.${userSelectNoneClass}`);
            if (!el) {
              document.head.appendChild(userSelectNoneEl);
            }
            break;
          }
          case 'pen': {
            props.setCustomTextSelection(true);
            const el = document.querySelector(`.${userSelectNoneClass}`);
            if (!el) {
              document.head.appendChild(userSelectNoneEl);
            }
            break;
          }
        }
      }
    };

    public display(request: any) {
      return super.display(request).then((r: any) => {
        const iframe: HTMLIFrameElement | undefined = this['iframe'];

        if (iframe && iframe.contentDocument) {
          const document = iframe.contentDocument;
          if (document.head) {
            document.head.appendChild(fontFaceEl);
            this.updateDisableTextSelect();
          }

          pointerEvents.forEach((event) => {
            document.removeEventListener(
              event as 'pointerup',
              props.onPointerEvent as any
            );
            document.addEventListener(
              event as 'pointerup',
              props.onPointerEvent as any,
              {
                passive: true,
              }
            );
          });

          document.addEventListener(
            'pointerdown',
            this.handleCustomeTextSelection
          );

          if (this.mc) {
            this.mc.off('tap', this.tapListener);
            this.mc.destroy();
          }
          const mc = (this.mc = new Hammer(document as any, {}));
          mc.on('tap', this.tapListener);

          if (isTouchDevice()) {
            const detectSwipe = (
              onSwipe: (direction: string) => void,
              deltaMin = 90
            ) => {
              let touchstart = { x: 0, y: 0 };
              let touchend = { x: 0, y: 0 };
              const directions = {
                RIGHT: 'right',
                LEFT: 'left',
              };
              let direction = null;

              const onTouchStart = (e: TouchEvent) => {
                touchstart.x = e.changedTouches[0].screenX;
                touchstart.y = e.changedTouches[0].screenY;
              };

              const onTouchEnd = (e: TouchEvent) => {
                touchend.x = e.changedTouches[0].screenX;
                touchend.y = e.changedTouches[0].screenY;
                const diffX = Math.abs(touchend.x - touchstart.x);

                if (diffX < deltaMin) {
                  return;
                }

                if (touchend.x <= touchstart.x) {
                  direction = directions.LEFT;
                }

                if (touchend.x >= touchstart.x) {
                  direction = directions.RIGHT;
                }

                if (direction && typeof onSwipe === 'function') {
                  onSwipe(direction);
                }

                direction = null;
                touchstart = { x: 0, y: 0 };
                touchend = { x: 0, y: 0 };
              };

              document.addEventListener('touchstart', onTouchStart, false);

              document.addEventListener('touchend', onTouchEnd, false);
            };

            detectSwipe((dir) => {
              if (dir === 'right') {
                props.prevPage();
              } else if (dir === 'left') {
                props.nextPage();
              }
            });
          }
        }

        if (iframe) {
          if (props.onKeyUp && iframe.contentWindow) {
            iframe.contentWindow.addEventListener('keyup', props.onKeyUp);
          }
          if (props.onBeforeDisplay) {
            props.onBeforeDisplay(iframe);
          }
        }

        return r;
      });
    }
  };

// const epubContainerClass = css({
//   '& iframe': {},
// });

// WARNING - CHANGING THIS VALUE WILL CHANGE PAGE NUMBER LOCATIONS FOR EPUBS
// READSTATS AND ANNOTATIONS DEPEND ON THIS!!!
const breakSize = 1000;

const getLocations = async (
  book: Book,
  epubPages?: string,
  onUpdateEpubPages?: (locations: string) => void
) => {
  if (epubPages) {
    book.locations.load(epubPages);
    return book.locations._locations;
  }
  const bookLocations = await book.locations.generate(breakSize);
  onUpdateEpubPages && onUpdateEpubPages(book.locations.save());

  return bookLocations;
};

interface State {
  containerDiv?: HTMLDivElement;
  dictionaryPopover?: React.ReactNode;
  location: string;
  pageNumber?: number;
  portal?: React.ReactNode;
  loaded: boolean;
  toc: TOC;
  selectedCfiRange?: string;
  epubWindow?: Window;
  currentView?: HTMLElement;
  selectionState?: {
    rangeBounds?: DOMRect;
    nativeRange?: Range;
    rangyRange?: RangyRange;
    selectedText?: string;
  };
  selectedAnnotation?: Annotation;
  selectedAnnotationRange?: Range;
  rendition?: Rendition;
  currentSection?: Section;
  currentContents?: Contents;
  annotationsKey?: string; // Change this to re-render annotations
}

export const EpubViewer: React.FC<EpubReaderProps> = (props) => {
  const selectedAnnotation = useSelector(
    annotationsSelectors.getSelectedAnnotation
  );
  let containerRef = React.useRef<HTMLDivElement>();

  const [pageNumber, setPageNumber] = React.useState<number>(1);
  const [epubWindow, setEpubWindow] = React.useState<Window>();
  const [currentView, setCurrentView] = React.useState<HTMLElement>();
  const [currentSection, setCurrentSection] = React.useState<Section>();
  const [currentContents, setCurrentContents] = React.useState<Contents>();

  const [rendition, setRendition] = React.useState<Rendition>();

  const [book, setBook] = React.useState<Book>();

  const { data: epubData, setLocation } = React.useContext(
    DocumentWithExtrasContext
  );
  const { setSelectionState } = React.useContext(TextSelectionContext);

  const [isInitialized, setIsInitialized] = React.useState<
    'UNITIALIZED' | 'STARTED' | 'INITIALIZED'
  >('UNITIALIZED');
  const appData = useSelector(applicationSelectors.getApplcationState);
  const readerData = useSelector(readerSelectors.getReaderData);
  const bookmarksData = useSelector(bookmarksSelectors.getBookmarks);
  const dispatch = useAppDispatch();

  const selectedAnnotationRef = React.useRef(selectedAnnotation);
  const propsRef = React.useRef(props);

  React.useEffect(() => {
    // we need to do this so the event created in the initReader always has the current value of selectedAnnotation
    selectedAnnotationRef.current = selectedAnnotation;
  }, [selectedAnnotation]);

  React.useEffect(() => {
    // we need to do this so the event created in the initReader always has the current value of selectedAnnotation
    propsRef.current = props;
  }, [props]);

  const [customTextSelection, setCustomTextSelection] =
    React.useState<boolean>(false);

  const initReader = async (container: HTMLElement, book: Book, toc: TOC) => {
    const rendition = book.renderTo(container, {
      contained: true,
      height: props.height,
      width: props.width,

      view: makeCustomIframeView({
        nextPage: nextPage,
        prevPage: prevPage,
        control: props.control,
        getCustomTextSelection: () => customTextSelection,
        setCustomTextSelection: setCustomTextSelection,
        onBeforeDisplay: props.onBeforeDisplay,
        onKeyUp: props.onKeyUp,
        onTap: (e, element: HTMLElement) => {
          if (selectedAnnotationRef.current?.id) {
            dispatch(annotationsActions.setSelectedAnnotation(''));
            return;
          }

          // this is such a hack.
          // epubjs has an event for markClicked (meaning a highlight is clicked)
          // which only fires when a mark is clicked.
          // There's no way to tell if we have clicked a mark or not in the same event.
          // so we just have to use a normal click (tap) event and make sure it runs after the markClicked event
          // to tell if an annotation was selected with the click.

          rendition.on('markClicked', (cfiRange, data) => {
            console.log("Clicked:", cfiRange)
          })
          
          setTimeout(() => {
            if (!selectedAnnotationRef.current?.id) {
              const pages = document.getElementsByTagName('iframe');
              let firstPage = element?.id === pages[0]?.id;
              let lastPage = element?.id === pages[pages.length - 1]?.id;

              const width = Math.min(element.offsetWidth, props.width); //if element width is greater than screen wdith, then just use screen width

              const pageFlipperWidth = getPageFlipperWidth(width);
              const x = e.center.x % width;

              if (x < pageFlipperWidth && firstPage) {
                prevPage();
              } else if (x > width - pageFlipperWidth && lastPage) {
                nextPage();
              } else if (
                !(
                  e.target.closest('div.toolbar') ||
                  e.target.closest('div.modal')
                )
              ) {
                props.onToggleToolbars();
              }
            }
          }, 100);
        },
      }),
    });

    if (props.checkOnline) {
      props.checkOnline();
    }

    if (props.themes) {
      rendition.themes.registerThemes(props.themes);
    }

    updateRenditionStyle(rendition);

    const isLinear = (item: EpubTOCItem) => {
      const spineIndex = book.spine.spineById[item.id];
      if (spineIndex === undefined) {
        return false;
      }
      const spineItem = book.spine.spineItems[spineIndex];
      if (!spineItem) {
        return false;
      }
      return spineItem.linear;
    };

    const epubTocToReaderToc = (epubTocItem: EpubTOCItem): TOCItem[] =>
      isLinear(epubTocItem)
        ? [
            {
              dest: epubTocItem.href,
              items: flatMap(epubTocToReaderToc, epubTocItem.subitems),
              newWindow: false,
              title: epubTocItem.label,
            },
          ]
        : [];

    if (props.onTocChange) {
      props.onTocChange(flatMap(epubTocToReaderToc, toc));
    }

    setRendition(rendition);
  };

  const { controller } = useEpubFindController(book, rendition);

  React.useEffect(() => {
    if (rendition && book) {
      props.onFindController(controller);
    }
  }, [controller]);
  React.useEffect(() => {
    const init = async () => {
      const book = ePub(epubData as string, {
        encoding: 'binary',
      }) as any as Book;

      book.loaded.navigation
        .then((nav) => {
          return nav;
        })
        .then(async (nav) => {
          initReader(containerRef.current, book, nav.toc);
          await getLocations(
            book,
            props.epubPages,
            props.onUpdateEpubPages
          ).then((locs) => {
            if (props.onPageLabelsChange) {
              props.onPageLabelsChange(
                locs.map((item, i) => ({ value: item, label: `${i + 1}` }))
              );
            }
          });

          if (book.rendition) {
            const looksLikeCfi = (val?: string) =>
              typeof val === 'string' && val.indexOf('epubcfi(') !== -1;
            const toCfiFromPageNumber = (val?: string): string | undefined => {
              if (!val || !/^\d+$/.test(val)) return undefined;
              try {
                const idx = Math.max(0, parseInt(val, 10) - 1);
                // @ts-ignore epubjs provides this at runtime
                return book.locations.cfiFromLocation(idx);
              } catch (e) {
                return undefined;
              }
            };

            const resolveInitial = (): string | undefined => {
              if (looksLikeCfi(props.initialLocation))
                return props.initialLocation as string;
              const fromPageParam = toCfiFromPageNumber(
                props.initialLocation as string
              );
              if (fromPageParam) return fromPageParam;
              if (looksLikeCfi(readerData?.viewState?.location))
                return readerData?.viewState?.location as string;
              return undefined;
            };

            const effectiveInitial = resolveInitial();

            if (book.rendition.location) {
              try {
                const target =
                  effectiveInitial || book.rendition.location?.start?.cfi;
                book.locations.setCurrent(
                  book.locations.locationFromCfi(target)
                );
              } catch (e) {}
            }

            try {
              if (effectiveInitial) {
                await book.rendition.display(effectiveInitial);
                setLocation(effectiveInitial, rendition);
              } else {
                await book.rendition.display();

                setLocation(
                  book.rendition.location?.start?.cfi,
                  rendition,
                  book.rendition.location?.end?.cfi
                );
              }
            } catch (e) {
              await book.rendition.display();
              setLocation(
                book.rendition.location?.start?.cfi,
                rendition,
                book.rendition.location?.end?.cfi
              );
            }
          }

          setBook(book);
          setIsInitialized('INITIALIZED');
        });
    };
    if (containerRef.current && epubData && isInitialized === 'UNITIALIZED') {
      setIsInitialized('STARTED');
      init();
    }
  }, [!!containerRef.current, epubData]);

  React.useEffect(() => {
    if (bookmarksData?.length && book && isInitialized === 'INITIALIZED') {
      bookmarksData.forEach((bookmark) => {
        const label = getPageLabelByString(bookmark.location, book.locations);

        dispatch(
          bookmarksActions.updateBookmarkPageLabels({ id: bookmark.id, label })
        );
      });
    }
  }, [book, bookmarksData, isInitialized]);

  React.useEffect(() => {
    if (!rendition) {
      return;
    }
    if (props.control?.type && isInitialized === 'INITIALIZED') {
      clearEpubSelection();
      const annoId = selectedAnnotation?.id;
      props.onDeselectAnnotation();
      switch (props.control.type) {
        case 'next':
          rendition.next();
          break;
        case 'prev':
          rendition.prev();
          break;
        case 'gotoToc':
          rendition.display(props.control['payload']['dest']);
          break;
        case 'changeLocation':
          rendition.display(props.control['payload']['location']).then(() => {
            annoId &&
              dispatch(annotationsActions.setSelectedAnnotation(annoId));
          });

          break;
      }
    }
  }, [props.control, isInitialized]);

  React.useEffect(() => {
    refreshStyleAndAnnotations();
    //renderSelectedAnnotationPopover();
    refreshRenditionSize();
  }, []);

  React.useEffect(() => {
    if (!rendition) {
      return;
    }

    //set up listeners.
    onRelocated(rendition, (d) => {
      try {
        dispatch(
          reportActions.asyncActions.createReadStat({
            type: 'pageOpened',
            cfiStart: d.start.cfi,
            cfiEnd: d.end.cfi,
          })
        );

        setLocation(d.start.cfi, rendition, d.end.cfi);
      } catch (e) {
        rcLogger.warn(e);
      }
    });

    onMarkClicked(rendition, (cfirange) => {
      const annotation = propsRef.current?.annotations.find(
        (anno) =>
          'location' in anno &&
          !!anno.location &&
          'range' in anno.location &&
          anno.location.range === cfirange.toString()
      );

      if (annotation && selectedAnnotationRef.current?.id !== annotation?.id) {
        dispatch(annotationsActions.setSelectedAnnotation(annotation.id));
      }
    });
  }, [rendition]);

  React.useEffect(() => {
    rendition &&
      onRendered(rendition, (section, view) => {
        if (customTextSelection && view.element) {
          view.element.addEventListener('contextmenu', preventDefault);
        }
        if (
          props.onSectionChange &&
          (!currentSection ||
            (currentSection.href !== section.href && view.window))
        ) {
          props.onSectionChange(view.window);
        }

        setEpubWindow(view.window);
        setCurrentView(view.element);
        setCurrentSection(section);
        setCurrentContents(view.contents);
        renderAnnotations();
      });
  }, [rendition]);

  const renderAnnotations = () => {
    if (!rendition) {
      return;
    }
    // TODO: Perhaps this algorithm can be improved:
    /*
      Rendering annotations state algo:
        - Clear all annotations on page
        - Add them all back
    */

    const locations = props.annotations.map((anno) => {
      if (
        !('location' in anno) ||
        !anno.location ||
        !('range' in anno.location) ||
        !anno.location.range
      ) {
        return '';
      }

      return anno.location.range;
    });

    locations.forEach((location) => {
      rendition.annotations.remove(location);
    });

    props.annotations.forEach((annotation) => {
      if (
        !('location' in annotation) ||
        !annotation.location ||
        !('range' in annotation.location) ||
        !annotation.location.range
      ) {
        return;
      }
      try {
        rendition.annotations.highlight(
          annotation.location.range,
          undefined,
          undefined,
          undefined,
          {
            fill: annotation.color,
          }
        );
      } catch (e) {}
    });
  };

  const lockFocus = () => {
    document.body.addEventListener('blur', (e) => {
      e.preventDefault();
      e.stopPropagation();
    });
  };

  const refreshRenditionSize = () => {
    if (!rendition) {
      return;
    }
    lockFocus();
    rendition.resize(props.width, props.height);
  };

  const updateRenditionStyle = (rendition: Rendition) => {
    if (!rendition) {
      return;
    }
    if (props.fontSize) {
      rendition.themes.override('font-size', props.fontSize);
    }
    if (props.fontFamily) {
      rendition.themes.override('font-family', props.fontFamily);
    }
    if (props.theme) {
      rendition.themes.select(props.theme);
    }
    if (rendition.location) {
      rendition.display(rendition.location.start.cfi);
    }
  };

  const refreshStyleAndAnnotations = () => {
    updateRenditionStyle(rendition);
    renderAnnotations();
  };

  React.useEffect(() => {
    refreshRenditionSize();
  }, [props.height, props.width]);

  React.useEffect(() => {
    renderAnnotations();
  }, [props.annotations.length, props.annotations]);

  React.useEffect(() => {
    refreshStyleAndAnnotations();
  }, [props.fontFamily, props.fontSize, props.theme]);

  const nextPage = () => {
    props.setDocumentControl({ type: 'next' });
  };
  const prevPage = () => {
    props.setDocumentControl({ type: 'prev' });
  };

  const clearEpubSelection = () =>
    epubWindow ? clearSelection(epubWindow) : undefined;

  useEpubTextSelectionEventListeners({
    window: epubWindow,
    pageNumber: pageNumber,
    section: currentSection,
    contents: currentContents,
  });

  const mapElementBounds = (el: HTMLElement): DOMRect => {
    const rect = el.getBoundingClientRect();
    return {
      width: rect.width,
      height: rect.height,
      top: Math.abs(rect.top),
      left: Math.abs(rect.left),
      y: Math.abs(rect.top),
      x: Math.abs(rect.left),
      get bottom() {
        return this.height + this.top;
      },
      get right() {
        return this.width + this.left;
      },
      toJSON() {
        return this;
      },
    };
  };

  return (
    <div className={C('position-relative')} ref={containerRef} style={{ paddingTop: 'env(safe-area-inset-top)', paddingBottom: 'env(safe-area-inset-bottom)' }}>
      {currentView && epubWindow && (
        <SelectionStream
          //Elements with class ".noSwipe" are excluded from jquery-touchswipe
          //http://labs.rampinteractive.co.uk/touchSwipe/docs/tutorial-Excluded_children.html
          markersClassName="noSwipe"
          custom={customTextSelection}
          mapElementBounds={mapElementBounds}
          boundsElement={currentView}
          element={epubWindow.document.body}
          window={epubWindow}
          markersContainer={containerRef.current}
          selectionCanvasContainer={containerRef.current}
          pointerEventsBounds={currentView}
          onSelectionStateChange={setSelectionState}
          location={readerData?.viewState?.location}
        ></SelectionStream>
      )}
      <ConnectedAnnotationPopover
        selectedAnnotation={selectedAnnotation as TextHighlightAnnotation}
        container={currentView}
        positionFixed
        deselectAnnotation={() =>
          dispatch(annotationsActions.setSelectedAnnotation(undefined))
        }
      />
      <ConnectedLearningToolbar container={currentView} positionFixed />
      <ModalManager />
      <LoadingIcon position="fixed" loading={isInitialized !== 'INITIALIZED'} />
    </div>
  );
};
